import React, { useContext, useEffect, useState } from "react";
import { Modal, TableRow } from "@mui/material";
import {
    ButtonRow,
    Expandable,
    InfoText,
    ModalWrapper,
    StyledBackdrop,
    SummaryTableCell,
    SummaryTableRow,
    TableWarningMessage,
    TagName,
    TitleText,
    TitleWrapper,
} from "./Styled";
import { api } from "../../../../shared/hooks/api";
import CustomTable from "../../../../shared/components/CustomTable";
import { Product } from "../../../../data/entities";
import { EnhancedProduct } from "../types";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import { OpenStateProductTableColumns, ProductOrderProductTableColumns } from "../../../../configs/productOrder";
import SearchBarWithSuggestion from "../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import IconButton from "../../../../shared/components/IconButton";
import { sum } from "lodash";
import { resolveCustomerProductPrice } from "../../../../utils/customerProductPricing";
import TextBox from "../../../../shared/components/TextBox";
import { StyledButton } from "../../../../shared/styled/TitleBar";
import { color } from "../../../../shared/styles";
import { formatCurrency, formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import useMergeState from "../../../../shared/hooks/useMergeState";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { ROLE } from "../../../../data/enums";
import { error } from "../../../../utils/toast";

// import AddAddonsModal from "../ProductList/AddonsModal";

interface ProductRowProps {
    item: any;
    extraRowProps?: any;
}

const getSelectedQuantity = (item: any) => {
    return item.selectedQuantity !== undefined ? item.selectedQuantity : 0;
};

const getMissingQuantity = (item: any) => {
    return Math.abs(Math.min(0, item.quantity - getSelectedQuantity(item)));
};

const ProductRow = ({ item, extraRowProps }: ProductRowProps) => {
    const quantity = getSelectedQuantity(item);
    const isEditingAllowed = !extraRowProps?.isView;
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canSeePrice = role !== ROLE.FIRST_STORAGE_KEEPER;

    const isQuanitySufficient = item.missingQuantity === 0 || getMissingQuantity(item) === 0;
    const backgroundColor = isQuanitySufficient ? "" : color.status.DANGER_LIGHT;
    const selectedQuantityBorderColor = isQuanitySufficient ? "" : color.status.FAIL;

    const percentDiscount = item.discount !== undefined ? item.discount : 0;
    const percentMarkup = item.markup !== undefined ? item.markup : 0;

    const price = item.item_type === "material"
        ? (item.material_price !== undefined ? item.material_price : 0)
        : (item.product_price !== undefined ? item.product_price : 0);

    const updateMaterialPrice = (value: string) => {
        if (extraRowProps && extraRowProps.updateMaterialPrice) {
            extraRowProps.updateMaterialPrice(item.id, Number(value));
        }
    };

    return (
        <>
            {!item.isSummary ? (
                <TableRow sx={{ backgroundColor }}>
                    <StyledTableCell align="left" style={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: backgroundColor || 'white' }}>
                        {item?.material_code || item?.product_code || "Undefined"}
                    </StyledTableCell>
                    <StyledTableCell align="left" style={{ position: 'sticky', left: 140, zIndex: 1, backgroundColor: backgroundColor || 'white' }}>
                        {item.name}
                        {item.item_type && (
                            <TagName itemType={item.item_type}>
                                {item.item_type === "product" ? "Sản phẩm" : "Vật tư"}
                            </TagName>
                        )}
                    </StyledTableCell>
                    {canSeePrice && (
                        <StyledTableCell align="left" style={{ position: 'sticky', left: 280, zIndex: 1, backgroundColor: backgroundColor || 'white' }}>
                            <TextBox
                                numeric
                                style={{
                                    borderColor: selectedQuantityBorderColor,
                                    backgroundColor,
                                    width: "160px",
                                    minWidth: "160px",
                                }}
                                text={formatInternationalNumber(price ?? "")}
                                onChangeText={(value: string) => {
                                    const n = parseInternationalToNumber(value, error);
                                    if (!Number.isFinite(n)) return;
                                    if (item.item_type === "material") {
                                        if (extraRowProps && extraRowProps.updateMaterialPrice) {
                                            extraRowProps.updateMaterialPrice(item.id, n);
                                        }
                                    } else {
                                        if (extraRowProps && extraRowProps.updateProductPrice) {
                                            extraRowProps.updateProductPrice(item, n);
                                        }
                                    }
                                }}
                            />
                        </StyledTableCell>
                    )}

                    {canSeePrice && isEditingAllowed && (
                        <>
                            <StyledTableCell align="left">
                                <TextBox
                                    numeric
                                    style={{
                                        borderColor: selectedQuantityBorderColor,
                                        backgroundColor,
                                    }}
                                    text={formatInternationalNumber(percentDiscount ?? "")}
                                    onChangeText={(value: string) => {
                                        const n = parseInternationalToNumber(value, error);
                                        if (!Number.isFinite(n)) return;
                                        extraRowProps.onChangeProductDiscount(item, n);
                                    }}
                                    disabled={extraRowProps.isView}
                                />
                            </StyledTableCell>
                            <StyledTableCell align="left">
                                <TextBox
                                    numeric
                                    style={{
                                        borderColor: selectedQuantityBorderColor,
                                        backgroundColor,
                                    }}
                                    text={formatInternationalNumber(percentMarkup ?? "")}
                                    onChangeText={(value: string) => {
                                        const n = parseInternationalToNumber(value, error);
                                        if (!Number.isFinite(n)) return;
                                        extraRowProps.onChangeProductMarkup(item, n);
                                    }}
                                    disabled={extraRowProps.isView}
                                />
                            </StyledTableCell>
                            <StyledTableCell align="left">
                                {formatCurrency(
                                    Math.ceil(((price * (100 - percentDiscount || 0)) / 100) * ((100 + percentMarkup || 0) / 100)),
                                )}
                            </StyledTableCell>
                        </>
                    )}

                    <StyledTableCell align="left">{item.quantity}</StyledTableCell>
                    <StyledTableCell align="left">
                        <TextBox
                            numeric
                            style={{
                                borderColor: selectedQuantityBorderColor,
                                backgroundColor,
                            }}
                            text={formatInternationalNumber(quantity ?? "")}
                            onChangeText={(value: string) => {
                                const n = parseInternationalToNumber(value, error);
                                if (!Number.isFinite(n)) return;
                                extraRowProps.updateItemSelectedQuantity(item, n);
                            }}
                        />
                    </StyledTableCell>

                    {extraRowProps.isView && (
                        <StyledTableCell align="left">
                            {item.missingQuantity !== undefined ? item.missingQuantity : getMissingQuantity(item)}
                        </StyledTableCell>
                    )}

                    <StyledTableCell align="left">
                        <IconButton onClick={() => extraRowProps.removeItem(item)}>
                            <DeleteIcon />
                        </IconButton>
                    </StyledTableCell>
                </TableRow>
            ) : (
                <SummaryTableRow>
                    <SummaryTableCell align="left" style={{ position: 'sticky', left: 0, zIndex: 12, backgroundColor: color.secondary.LIGHT_GREY }}>{item.id}</SummaryTableCell>
                    <SummaryTableCell align="left" style={{ position: 'sticky', left: 140, zIndex: 12, backgroundColor: color.secondary.LIGHT_GREY }}>{item.name}</SummaryTableCell>
                    {canSeePrice && (
                        <SummaryTableCell align="left" style={{ position: 'sticky', left: 280, zIndex: 12, backgroundColor: color.secondary.LIGHT_GREY }}>{formatCurrency(item.totalOriginalPrice)}</SummaryTableCell>
                    )}
                    {canSeePrice && isEditingAllowed && (
                        <SummaryTableCell align="left"></SummaryTableCell>
                    )}
                    {canSeePrice && isEditingAllowed && (
                        <SummaryTableCell align="left"></SummaryTableCell>
                    )}
                    {canSeePrice && isEditingAllowed && (
                        <SummaryTableCell align="left">{formatCurrency(item.totalDiscountPrice)}</SummaryTableCell>
                    )}
                    <SummaryTableCell align="left"></SummaryTableCell>
                    <SummaryTableCell align="left"></SummaryTableCell>
                    {extraRowProps.isView && <SummaryTableCell align="left"></SummaryTableCell>}
                    <SummaryTableCell align="left"></SummaryTableCell>
                </SummaryTableRow>
            )}
        </>
    );
};

interface AddProductModalProps {
    onChangeProductSelectedQuantity: Function;
    selectedProducts: any[];
    open: boolean;
    handleClose: any;
    mergeProductOrder: Function;
    isView?: boolean;
    customerCategory?: string;
    customerId?: string;
    orderState?: string;
}

const AddProductModal = ({
                             selectedProducts,
                             open,
                             handleClose,
                             mergeProductOrder,
                             isView,
                             customerCategory,
                             customerId,
                             orderState = "OPEN",
                         }: AddProductModalProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [searchItems, setSearchItems] = useState<EnhancedProduct[]>([]);
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canSeePrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    const productsApiUrl = `/customers/${customerId}/products/?is_active=true&page_size=${Number.MAX_SAFE_INTEGER}${
        customerCategory ? "&category=" + customerCategory.split(";").join(",") : ""
    }`;

    const materialsApiUrl = `/materials?is_active=true&page_size=${Number.MAX_SAFE_INTEGER}`;

    const [{ data: productsData }, fetchProducts] = api.get(productsApiUrl, undefined, true);
    const [{ data: materialsData }, fetchMaterials] = api.get(materialsApiUrl, undefined, true);

    const [selectedItems, setSelectedItems] = useMergeState(selectedProducts);

    useEffect(() => {
        const products = productsData && productsData.results ?
            productsData.results.map((product: Product) => ({
                ...product,
                item_type: "product",
                product_price: resolveCustomerProductPrice(product),
            })) : [];

        const materials = materialsData && materialsData.results ?
            materialsData.results.map((material: any) => ({
                ...material,
                item_type: "material",
                material_price: material.material_price || 0,
            })) : [];

        setSearchItems([...products, ...materials] as EnhancedProduct[]);
    }, [productsData, materialsData]);

    const summaryProduct = {
        id: `Tổng ${selectedItems.length} mục`,
        name: "Subtotal",
        totalDiscountPrice: sum(
            selectedItems.map((item: any) => {
                const discount = item.discount || 0;
                const markup = item.markup || 0;
                const itemPrice = item.item_type === "material" ? item.material_price : item.product_price;
                const discountedPrice = Math.ceil(((itemPrice * (100 - discount)) / 100) * ((100 + markup) / 100));
                return discountedPrice * (item.selectedQuantity || 0);
            }),
        ),
        totalOriginalPrice: sum(
            selectedItems.map((item: any) => {
                const itemPrice = item.item_type === "material" ? item.material_price : item.product_price;
                return (item.selectedQuantity || 0) * (itemPrice || 0);
            }),
        ),
        isSummary: true,
    };
    const tableItems = selectedItems.length === 0 ? [] : [...selectedItems, summaryProduct];

    const isSufficient = tableItems.every(
        (item: any) => item.isSummary || item.missingQuantity === 0 || getMissingQuantity(item) === 0,
    );

    const removeSelectedItem = (removedItem: EnhancedProduct) => {
        const removedSelectedItems = selectedItems.filter((selectedItem: EnhancedProduct) => {
            if ((removedItem as any).line_item_id) {
                return (selectedItem as any).line_item_id !== (removedItem as any).line_item_id;
            }
            if ((removedItem as any).client_uid) {
                return (selectedItem as any).client_uid !== (removedItem as any).client_uid;
            }
            return !(selectedItem.id === removedItem.id && selectedItem.item_type === removedItem.item_type);
        });
        setSelectedItems(removedSelectedItems);
    };

    const updateItemSelectedQuantity = (item: EnhancedProduct, selectedQuantity: number) => {
        const updatedSelectedItems = selectedItems.map((product: any) => {
            const matches =
                ((item as any).line_item_id && (product as any).line_item_id === (item as any).line_item_id) ||
                ((item as any).client_uid && (product as any).client_uid === (item as any).client_uid);
            if (matches) {
                return {
                    ...product,
                    selectedQuantity,
                };
            }
            return product;
        });
        setSelectedItems(updatedSelectedItems);
    };

    const updateItemAddOns = (item: EnhancedProduct, addons: any[]) => {
        const updatedSelectedItems = selectedItems.map((product: any) => {
            if (product.id === item.id) {
                return { ...item, addons };
            }

            return product;
        });

        setSelectedItems(updatedSelectedItems);
    };

    const onChangeProductDiscount = (target: any, discount: any) => {
        const updatedSelectedItems = selectedItems.map((item: any) => {
            const matches =
                ((target as any).line_item_id && (item as any).line_item_id === (target as any).line_item_id) ||
                ((target as any).client_uid && (item as any).client_uid === (target as any).client_uid);
            if (matches) {
                return {
                    ...item,
                    discount,
                };
            }
            return item;
        });
        setSelectedItems(updatedSelectedItems);
    };

    const onChangeProductMarkup = (target: any, markup: any) => {
        const updatedSelectedItems = selectedItems.map((item: any) => {
            const matches =
                ((target as any).line_item_id && (item as any).line_item_id === (target as any).line_item_id) ||
                ((target as any).client_uid && (item as any).client_uid === (target as any).client_uid);
            if (matches) {
                return {
                    ...item,
                    markup,
                };
            }
            return item;
        });
        setSelectedItems(updatedSelectedItems);
    };

    const updateMaterialPrice = (targetId: string, materialPrice: number) => {
        const updatedSelectedItems = selectedItems.map((item: any) => {
            if (item.id === targetId) {
                return {
                    ...item,
                    material_price: materialPrice,
                };
            }
            return item;
        });
        setSelectedItems(updatedSelectedItems);
    };

    const updateProductPrice = (target: any, productPrice: number) => {
        const updatedSelectedItems = selectedItems.map((item: any) => {
            const matches =
                ((target as any).line_item_id && (item as any).line_item_id === (target as any).line_item_id) ||
                ((target as any).client_uid && (item as any).client_uid === (target as any).client_uid);
            if (matches) {
                return { ...item, product_price: productPrice };
            }
            return item;
        });
        setSelectedItems(updatedSelectedItems);
    };

    const handleOnclickItem = (item: EnhancedProduct, isAdded: boolean) => {
        const isItemContained = selectedItems.some((selectedItem: any) =>
            (selectedItem.line_item_id && selectedItem.line_item_id === (item as any).line_item_id) ||
            (selectedItem.id === item.id && selectedItem.item_type === item.item_type),
        );
        if (isAdded && !isItemContained) {
            setSelectedItems([
                ...selectedItems,
                {
                    ...item,
                    client_uid: item.client_uid || `${item.item_type || "product"}:${item.id}:${Date.now()}`,
                    selectedQuantity: item.selectedQuantity ?? 0,
                },
            ]);
        } else if (!isAdded && isItemContained) {
            removeSelectedItem(item);
        }
    };

    const onClickAddProducts = () => {
        const typedItems = selectedItems as EnhancedProduct[];
        const newProducts = typedItems.filter(item => !item.item_type || item.item_type === "product");
        const newMaterials = typedItems.filter(item => item.item_type === "material");

        mergeProductOrder({
            products: newProducts.map((p: any) => ({
                ...p,
                client_uid: p.client_uid, // do not regenerate uid here
            })),
            materials: newMaterials.map((m: any) => ({
                ...m,
                client_uid: m.client_uid, // do not regenerate uid here
            })),
        });

        handleClose();
    };

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    useEffect(() => {
        if (open) {
            setIsOpen(true);
        } else {
            const timer = setTimeout(() => setIsOpen(false), 200);
            return () => clearTimeout(timer);
        }
    }, [open]);

    const getColumns = () => {
        const isEditingAllowed = !isView;
        const baseColumns = isEditingAllowed ? ProductOrderProductTableColumns : OpenStateProductTableColumns;
        const columns = isView ? baseColumns : baseColumns.filter((item) => item.text !== "Số lượng thiếu");
        if (!canSeePrice) {
            return columns.filter(
                (item) =>
                    item.text !== "Giá bán" &&
                    item.text !== "Chiết khấu (%)" &&
                    item.text !== "Thuế suất (%)" &&
                    item.text !== "Giá sau chiết khấu",
            );
        }
        return columns;
    };

    const fetchItems = (variables: any) => {
        fetchProducts({
            ...variables,
        });
        fetchMaterials({
            ...variables,
        });
    };

    return (
        <Modal
            open={open}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <ModalWrapper isOpen={isOpen}>
                <Expandable>
                    <TitleWrapper>
                        <TitleText>Thêm sản phẩm và vật tư</TitleText>
                        <InfoText>Vui lòng lựa chọn sản phẩm hoặc vật tư</InfoText>
                    </TitleWrapper>

                    <SearchBarWithSuggestion
                        menuList={searchItems}
                        selectedItems={selectedItems}
                        fetchItems={fetchItems}
                        handleOnclickItem={handleOnclickItem}
                        placeholder="Tìm kiếm theo từ khoá Mã sản phẩm, Tên sản phẩm, Mã vật tư, Tên vật tư"
                    />

                    {!isSufficient && (
                        <TableWarningMessage>
                            Cảnh báo số lượng yêu cầu vượt quá số lượng tồn kho. Cần sản xuất thêm sau khi tạo đơn hàng
                        </TableWarningMessage>
                    )}

                    <CustomTable
                        itemList={tableItems}
                        RowElement={ProductRow}
                        TableColumns={getColumns()}
                        extraRowProps={{
                            removeItem: removeSelectedItem,
                            updateItemSelectedQuantity,
                            onChangeProductDiscount,
                            onChangeProductMarkup,
                            updateItemAddOns,
                            updateMaterialPrice,
                            updateProductPrice,
                            isView,
                            orderState,
                        }}
                        containerStyle={{ maxHeight: "400px" }}
                    />
                </Expandable>

                <ButtonRow>
                    <StyledButton onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                    <StyledButton variant="contained" onClick={onClickAddProducts}>
                        Lưu
                    </StyledButton>
                </ButtonRow>
            </ModalWrapper>
        </Modal>
    );
};

export default AddProductModal;
